import {
  createContext,
  FC,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import {
  AuthContext,
  CommonContext,
  Currency,
  Game,
  GamesContext,
  randomNumber,
  useApi,
  User,
} from '@repo-level/core-front';
import { useTranslation } from 'react-i18next';
import {
  ascend,
  filter,
  isNil,
  map,
  partition,
  pipe,
  prop,
  propEq,
  sort,
  findLast,
} from 'ramda';
import moment from 'moment';

import { CMSGroupMode, CMSImageBase } from 'api/cms/base';
import { gameCovers } from 'api/cms/gameCovers';
import {
  CMSGameCategory,
  gameCategories,
  SeparatedCategoriesSection,
  separatedCategoriesSection,
} from 'api/cms/gameCategories';
import {
  bonusesToDepositCodes,
  bonusGroupsToDepositCodes,
  sortCovers,
} from 'contexts/addons/helper';
import {
  Bonus,
  bonuses as fetchBonuses,
  BonusGroup,
  bonusGroups as fetchBonusGroups,
  BonusI,
} from 'api/cms/bonuses';
import { filterBonuses, filterBonusGroups } from 'helpers/common';
import { CustomOffer, useCustomOffer } from 'hooks/customOffer';
import { jackpots as fetchJackpots, Jackpots } from 'api/games';
import { usePaymentLanguage } from 'hooks/payments';
import { useTheme } from 'hooks/theme';
import {
  filterGames,
  GameRecommendationCategories,
  getTopCategoryId,
  UpdatedGameCategory,
} from 'helpers/game';
import {
  filterByCountries,
  filterByGroupAndDuration,
  filterBySchedule,
} from 'helpers/cms';
import {
  getThirdPartySummaries,
  ThirdPartySummary,
} from 'api/cms/thirdPartySummaries';
import { getEnabledCurrenciesByCountry } from 'helpers/currency';
import { TournamentGames, tournamentGames } from 'api/cms/tournaments';
import {
  CompointsRatesMoney,
  compPointsRatesMoney,
  PlayerCompoints,
  playerCompoints,
} from 'api/common';
import { CookieType, pickProperties, useCookie } from 'hooks/common';
import { LocalStorage } from 'helpers/localStorage';
import { CmsABTest, cmsABTests } from 'api/cms/abTests';
import { abTestingFunction } from 'helpers/abTesting';
import { PaymentMethodsApi, paymentMethods } from 'api/cms/paymentMethods';
import { CMSGameTheme, gameThemes } from 'api/cms/gameThemes';

export enum KYCVersion {
  A_DEFAULT = 'a_default_kyc',
  B_TEST = 'b_test_kyc',
}

const SHOW_EMAIL_POPUP_AGAIN_AFTER_DAYS = 7;

export const lastSeenEmailConfirmationPopup = new LocalStorage<string, null>(
  'last-seen-email-confirmation-popup',
  null
);

const CONFIRMATION_EMAIL_ROUTE = '/users/confirmation';

const initialParamsForCustomBonus = {
  frequency: null,
  wager: null,
  minDep: null,
  additionalInfo: null,
  depNumber: null,
  fsAmount: null,
  game: null,
  maxBonusAmount: null,
  maxWinFs: null,
  wagerTime: null,

  cashier: true,
  promoPage: true,
  tcPage: true,

  showGuestToggle: true,
  groupsIncludeMode: CMSGroupMode.AND,
  groupsInclude: null,
  groupsExcludeMode: CMSGroupMode.AND,
  groupsExclude: null,
  durationFrom: null,
  durationTo: null,
};

export interface GameCovers {
  [key: string]: { cover: CMSImageBase };
}

export interface AddonsContextData {
  covers: GameCovers;
  pendingCovers: boolean;
  gameCategoriesList: UpdatedGameCategory[];
  pendingGameCategoriesList: boolean;
  separatedCategories: UpdatedGameCategory[];
  gameThemes: CMSGameTheme[];
  pendingGameThemes: boolean;
  depositCodes: string[];
  bonuses: Bonus[];
  pendingBonuses: boolean;
  bonusGroups: BonusGroup[];
  pendingBonusGroups: boolean;
  jackpots: Jackpots | undefined;
  customOffer: CustomOffer | undefined;
  enableCurrencies: Currency[];
  thirdPartySummaries: ThirdPartySummary[] | undefined;
  pendingThirdPartySummaries: boolean;
  separatedCategoriesSectionData: SeparatedCategoriesSection | undefined;
  separatedCategoriesSectionPending: boolean;
  depositBonuses: BonusI[];
  tournamentGames: TournamentGames | undefined;
  pendingTournamentGames: boolean;
  compointsRatesMoney: CompointsRatesMoney[] | undefined;
  pendingCompointsRatesMoney: boolean;
  playerCompoints: PlayerCompoints | undefined;
  fetchPlayerCompoints: () => void;
  pendingPlayerCompoints: boolean;
  isEmailConfirmationPopup: boolean;
  emailConfirmationPopupHeight: number;
  setEmailConfirmationPopupSeen: () => void;
  setEmailConfirmationPopupHeight: (height: number) => void;
  getFilteredByUserWalletsGames: (gamesToFilter: Game[]) => Game[];

  autoRegistrationData: Partial<User> | null;
  setAutoRegistrationData: (data: Partial<User> | null) => void;

  cmsABTests: CmsABTest[];
  kycVersion?: KYCVersion;

  localizedTopCategoryId: string;

  cmsPaymentMethods: PaymentMethodsApi | undefined;
}

export const AddonsContext = createContext<AddonsContextData>(null as any);

export const AddonsContextProvider: FC = ({ children }) => {
  const {
    i18n: { language },
  } = useTranslation();
  const { images } = useTheme();

  const { currentIp, currencies } = useContext(CommonContext);
  const { user, isAuthenticated, pendingUser, wallets } = useContext(
    AuthContext
  );
  const { categories: defaultCategories, pendingCategories } = useContext(
    GamesContext
  );

  usePaymentLanguage(language);

  useCookie({
    name: 'userDetails',
    value: {
      type: CookieType.Object,
      data: pickProperties(user, [
        'id',
        'email',
        'statuses',
        'language',
        'country',
      ]),
    },
    days: 90,
    encryptKey: 'QExMyj2jKBnWDGpv',
    withReplace: true,
  });

  const [
    autoRegistrationData,
    setAutoRegistrationData,
  ] = useState<Partial<User> | null>(null);

  const [requestGameCovers, { data: covers, pending: pendingCovers }] = useApi(
    gameCovers
  );
  const [
    requestGameCategories,
    { data: gameCategoriesCMS, pending: pendingGameCategoriesCMS },
  ] = useApi(gameCategories);

  const [
    requestGameThemes,
    { data: gameThemesCMS, pending: pendingGameThemesCMS },
  ] = useApi(gameThemes);

  const [requestCMSPaymentMethods, { data: cmsPaymentMethods }] = useApi(
    paymentMethods
  );

  const [requestJackpots, { data: jackpots }] = useApi(fetchJackpots);

  const [
    requestBonusGroups,
    { data: bonusGroupsData, pending: pendingBonusGroups },
  ] = useApi(fetchBonusGroups);

  const [
    requestBonuses,
    { data: bonusesData, pending: pendingBonuses },
  ] = useApi(fetchBonuses);

  const [
    requestTournamentGameIds,
    { data: tournamentGamesData, pending: pendingTournamentGames },
  ] = useApi(tournamentGames);

  const [
    requestSeparatedCategoriesSection,
    {
      data: separatedCategoriesSectionData,
      pending: separatedCategoriesSectionPending,
    },
  ] = useApi(separatedCategoriesSection);

  const [requestCmsABTests, { data: cmsABTestsData }] = useApi(cmsABTests);

  const [
    requestCompointsRatesMoney,
    { pending: pendingCompointsRatesMoney, data: compointsRatesMoneyData },
  ] = useApi(compPointsRatesMoney);

  const [playerCompointsS, setPlayerCompointsS] = useState<PlayerCompoints>();

  const [
    requestPlayerCompoints,
    { pending: pendingPlayerCompoints, data: playerCompointsData },
  ] = useApi(playerCompoints);

  const fetchPlayerCompoints = useCallback(() => {
    isAuthenticated && requestPlayerCompoints();
  }, [isAuthenticated, requestPlayerCompoints]);

  useEffect(() => {
    fetchPlayerCompoints();
  }, [fetchPlayerCompoints]);

  useEffect(() => {
    setPlayerCompointsS(playerCompointsData);
  }, [playerCompointsData]);

  useEffect(() => {
    requestBonuses(language);
    requestBonusGroups(language);
    requestGameCategories(language);
    requestGameThemes(language);
    requestSeparatedCategoriesSection(language);
    requestTournamentGameIds();
    requestCmsABTests();
  }, [
    requestBonusGroups,
    requestBonuses,
    requestGameCategories,
    requestGameThemes,
    language,
    requestSeparatedCategoriesSection,
    requestTournamentGameIds,
    requestCmsABTests,
  ]);

  useEffect(() => {
    requestGameCovers();
    requestJackpots();
  }, [requestGameCovers, requestJackpots]);

  useEffect(() => {
    if (!isAuthenticated) return;
    requestCompointsRatesMoney();
  }, [isAuthenticated, requestCompointsRatesMoney]);

  useEffect(() => {
    requestCMSPaymentMethods(language);
  }, [language, requestCMSPaymentMethods]);

  const customOffer = useCustomOffer();

  const bonuses = useMemo(() => {
    const customBonuses = (customOffer?.bonuses?.map((bonus) => ({
      id: randomNumber(100000, 1000000),
      ...initialParamsForCustomBonus,
      ...bonus,
    })) || []) as Bonus[];

    const mergeBonuses = [...customBonuses, ...(bonusesData || [])];

    return filterBonuses(mergeBonuses, user) as Bonus[];
  }, [bonusesData, user, customOffer]);

  const [
    requestThirdPartySummaries,
    { data: thirdPartySummaries, pending: pendingThirdPartySummaries },
    { reset: resetThirdPartySummaries },
  ] = useApi(getThirdPartySummaries);

  const kycVersion = useMemo(() => {
    if (!isAuthenticated) return;

    return abTestingFunction(
      'kyc_2_iteration',
      {
        defaultVersion: KYCVersion.A_DEFAULT,
        versions: [
          {
            name: KYCVersion.A_DEFAULT,
            probability: 50,
          },
          {
            name: KYCVersion.B_TEST,
            probability: 50,
          },
        ],
      },
      user,
      currentIp,
      cmsABTestsData || []
    ) as KYCVersion;
  }, [cmsABTestsData, currentIp, isAuthenticated, user]);

  const bonusGroups = useMemo(
    () => filterBonusGroups(bonusGroupsData || [], user),
    [bonusGroupsData, user]
  );

  const sortedCovers = useMemo(() => sortCovers(covers || []), [covers]);

  const depositCodes = useMemo(
    () => [
      ...bonusesToDepositCodes(bonusesData || []),
      ...bonusGroupsToDepositCodes(bonusGroupsData || []),
    ],
    [bonusesData, bonusGroupsData]
  );

  const backEndCategoriesIds = useMemo(
    () => defaultCategories.map(({ id }) => id),
    [defaultCategories]
  );

  const gameCategoriesListAll: UpdatedGameCategory[] = useMemo(
    () =>
      pipe<
        CMSGameCategory[],
        CMSGameCategory[],
        UpdatedGameCategory[],
        UpdatedGameCategory[],
        UpdatedGameCategory[],
        UpdatedGameCategory[],
        UpdatedGameCategory[]
      >(
        filter(({ categoryId }: CMSGameCategory) => {
          if (categoryId === GameRecommendationCategories.Favorites)
            return isAuthenticated;

          return backEndCategoriesIds.includes(categoryId);
        }),
        map(
          ({
            title,
            categoryId,
            order,
            groupsExcludeMode,
            groupsExclude,
            groupsInclude,
            groupsIncludeMode,
            durationFrom,
            durationTo,
            countryCodesExclude,
            countryCodesInclude,
            scheduler,
            description,
            icon,
            showSeparately,
          }: CMSGameCategory) => {
            const showGuestToggle = !groupsInclude?.trim().length;

            return {
              id: categoryId,
              title,
              description,
              icon: icon?.url || images.defaultCategoryIcon.path,
              href: `/search-games/${categoryId}`,
              order: isNil(order) ? defaultCategories.length - 1 : order,
              groupsIncludeMode: groupsIncludeMode || CMSGroupMode.AND,
              groupsInclude,
              groupsExcludeMode: groupsExcludeMode || CMSGroupMode.AND,
              groupsExclude,
              durationFrom,
              durationTo,
              showGuestToggle,
              countryCodesExclude,
              countryCodesInclude,
              scheduler,
              showSeparately: !!showSeparately,
            };
          }
        ),
        filterBySchedule,
        filterByGroupAndDuration<UpdatedGameCategory>({ user }),
        filterByCountries<UpdatedGameCategory>({
          countryCode: currentIp?.countryCode,
        }),
        sort(ascend(prop('order')))
      )(gameCategoriesCMS || []),
    [
      isAuthenticated,
      user,
      currentIp?.countryCode,
      defaultCategories.length,
      gameCategoriesCMS,
      backEndCategoriesIds,
      images.defaultCategoryIcon.path,
    ]
  );

  const sortedGameThemes: CMSGameTheme[] = useMemo(
    () =>
      pipe<CMSGameTheme[], CMSGameTheme[]>(sort(ascend(prop('order'))))(
        gameThemesCMS || []
      ),
    [gameThemesCMS]
  );

  const [separatedCategories, gameCategoriesList] = useMemo(
    () => partition(propEq('showSeparately', true), gameCategoriesListAll),
    [gameCategoriesListAll]
  );

  const localizedTopCategoryId = useMemo(
    () => getTopCategoryId(gameCategoriesList),
    [gameCategoriesList]
  );

  const enableCurrencies = useMemo(() => {
    if (currencies && currentIp) {
      return getEnabledCurrenciesByCountry(currencies, currentIp.countryCode);
    } else return [];
  }, [currentIp, currencies]);

  useEffect(() => {
    if (user?.id == null) {
      resetThirdPartySummaries();
      return;
    }

    requestThirdPartySummaries(user.id);
  }, [user?.id, resetThirdPartySummaries, requestThirdPartySummaries]);

  const depositBonuses = useMemo(() => {
    const bonusGroupsT = bonusGroups.flatMap(
      ({ bonuses, background, character }) =>
        bonuses
          .filter(({ cashier, code, isActive }) => cashier && code && isActive)
          .map((bonus) => ({ ...bonus, background, character }))
    );

    const activeBonusInGroup = findLast(
      ({ isActive }) => !!isActive,
      bonusGroupsT
    );

    const allBonuses = [
      ...(activeBonusInGroup ? [activeBonusInGroup] : []),
      ...bonuses.filter(({ cashier, code }) => cashier && code),
    ] as BonusI[];

    if (user?.depositBonusCode) {
      return allBonuses.filter(({ code }) => code === user.depositBonusCode);
    }

    return allBonuses.sort((a, b) => a.order - b.order);
  }, [bonuses, bonusGroups, user?.depositBonusCode]);

  const [isEmailConfirmationPopup, setIsEmailConfirmationPopup] = useState(
    false
  );

  const [
    emailConfirmationPopupHeight,
    setEmailConfirmationPopupHeight,
  ] = useState(0);

  const setEmailConfirmationPopupSeen = () => {
    lastSeenEmailConfirmationPopup.replace(moment().format());
    setIsEmailConfirmationPopup(false);
  };

  useEffect(() => {
    if (
      !isAuthenticated ||
      pendingUser ||
      user?.confirmedAt ||
      window.location.pathname === CONFIRMATION_EMAIL_ROUTE
    ) {
      setIsEmailConfirmationPopup(false);

      if (
        user?.confirmedAt ||
        window.location.pathname === CONFIRMATION_EMAIL_ROUTE
      )
        localStorage.removeItem(lastSeenEmailConfirmationPopup.key);

      return;
    }

    if (isAuthenticated && !user?.confirmedAt) {
      const isEmailConfirmationPopup =
        !lastSeenEmailConfirmationPopup.get() ||
        moment().diff(lastSeenEmailConfirmationPopup.get(), 'days') >=
          SHOW_EMAIL_POPUP_AGAIN_AFTER_DAYS;

      setIsEmailConfirmationPopup(isEmailConfirmationPopup);

      return;
    }
  }, [currentIp, isAuthenticated, pendingUser, user]);

  const getFilteredByUserWalletsGames = useCallback(
    (gamesToFilter: Game[]) => {
      if (!user) return gamesToFilter;

      const userCurrencies = wallets.map(({ currency }) => currency);

      const availableByCurrencyGames = gamesToFilter.filter(({ real }) => {
        const availableGameCurrencies = Object.keys(real);

        return availableGameCurrencies.some((currency) =>
          userCurrencies.includes(currency)
        );
      });

      return availableByCurrencyGames;
    },
    [user, wallets]
  );

  return (
    <AddonsContext.Provider
      value={{
        covers: sortedCovers,
        pendingCovers,
        gameThemes: sortedGameThemes,
        pendingGameThemes: pendingGameThemesCMS,
        gameCategoriesList,
        pendingGameCategoriesList:
          pendingGameCategoriesCMS || pendingCategories,
        separatedCategories,
        depositCodes,
        pendingBonuses,
        pendingBonusGroups,
        bonuses,
        bonusGroups,
        jackpots,
        pendingThirdPartySummaries,
        thirdPartySummaries,
        customOffer: customOffer,
        enableCurrencies,
        separatedCategoriesSectionData,
        separatedCategoriesSectionPending,
        depositBonuses,
        tournamentGames: tournamentGamesData,
        pendingTournamentGames,
        compointsRatesMoney: compointsRatesMoneyData,
        pendingCompointsRatesMoney,
        playerCompoints: playerCompointsS,
        fetchPlayerCompoints,
        pendingPlayerCompoints,
        isEmailConfirmationPopup,
        setEmailConfirmationPopupSeen,
        setEmailConfirmationPopupHeight,
        emailConfirmationPopupHeight,
        getFilteredByUserWalletsGames,
        autoRegistrationData,
        setAutoRegistrationData,
        cmsABTests: cmsABTestsData || [],
        kycVersion,
        localizedTopCategoryId,
        cmsPaymentMethods,
      }}
    >
      {children}
    </AddonsContext.Provider>
  );
};
